/**
 * Media Annotation & Comment Types
 * Angular port of Magic Patterns components/phases/media/types.ts.
 *
 * Coordinates are PERCENT (0-100) of the media element for BOTH annotations and
 * comments, so markup is resize-invariant (MP used px on canvas for annotations and
 * % for comments; we unify on % for the SVG-overlay clone).
 */

export type DrawingTool =
  | 'select' | 'pen' | 'arrow' | 'rectangle' | 'circle' | 'text' | 'ruler' | 'area';

export type AnnotationType =
  | 'draw' | 'arrow' | 'rectangle' | 'circle' | 'text' | 'ruler' | 'area';

export interface Point {
  x: number; // percent 0-100
  y: number; // percent 0-100
}

export interface Annotation {
  id: string;
  type: AnnotationType;
  color: string;
  fillColor?: string;
  opacity?: number;        // 0.1 - 1
  strokeWidth: number;     // px
  points?: Point[];        // freehand (pen)
  start?: Point;           // shapes / arrow / text / ruler / area
  end?: Point;
  text?: string;           // text annotations
  fontSize?: number;
  author: string;
  timestamp: string;       // ISO
  description?: string;
  locked?: boolean;
  measurementValue?: number; // ruler/area
  measurementUnit?: string;  // 'm' | 'm²'
}

export type CommentStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type CommentPriority = 'low' | 'medium' | 'high' | 'critical';

export interface CommentReply {
  id: string;
  text: string;
  author: string;
  timestamp: string;
}

export interface Comment {
  id: string;
  x: number; // percent 0-100 (image annotator); 0 for cell-anchored comments
  y: number; // percent 0-100 (image annotator); 0 for cell-anchored comments
  text: string;
  author: string;
  timestamp: string;
  status: CommentStatus;
  priority: CommentPriority;
  replies: CommentReply[];
  annotationId?: string;
  // Spreadsheet (CSV) anchoring — the cell a comment is attached to, e.g. "A1".
  cellRef?: string;
  sheetIndex?: number;
  // Audio/video anchoring — the playback position (seconds) the comment refers to.
  mediaTime?: number;
}

/** Palette shown in the annotation toolbar. */
export const ANNOTATION_COLORS: string[] = [
  '#FF6B35', '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#FFFFFF', '#000000'
];

/** Stroke widths shown in the toolbar. */
export const STROKE_WIDTHS: number[] = [2, 4, 6, 8, 12];
